// Карточка запроса разрешения. Рендерится прямо в потоке лога (kind="perm").
// Актуальность берём из стора: запрос «живой», пока он в pendingPermissions —
// это же список приходит в hello после реконнекта, поэтому карточка
// восстанавливается сама.

import { memo, useMemo, useState } from 'react'
import type { PermissionRequest } from '../protocol'
import { usePane } from '../store/logStore'
import type { LogLine, PermStatus } from '../store/logStore'
import type { PermissionOptions } from '../transport/ws'

interface Props {
  sessionId: string
  line: LogLine
  onRespond(id: string, behavior: 'allow' | 'deny', opts?: PermissionOptions): void
}

const BADGE: Record<PermStatus, string> = {
  allowed: 'разрешено',
  denied: 'запрещено',
  expired: 'истёк срок',
}

function formatArgs(input: Record<string, unknown>): string {
  try {
    return JSON.stringify(input, null, 2)
  } catch {
    return String(input)
  }
}

function PermissionCard({ sessionId, line, onRespond }: Props) {
  const perm = line.perm as PermissionRequest
  const open = usePane(sessionId, (s) => s.pending.some((p) => p.id === perm.id))
  const status = usePane(sessionId, (s) => s.permStatus[perm.id] as PermStatus | undefined)
  const greeted = usePane(sessionId, (s) => s.greeted)
  const [reason, setReason] = useState('')

  const args = useMemo(() => formatArgs(perm.input ?? {}), [perm.input])

  const badge = status
    ? BADGE[status]
    : open
      ? 'ждёт ответа'
      : greeted ? 'запрос закрыт' : 'ожидание'

  const badgeCls = status
    ? ` ab-perm__badge--${status === 'allowed' ? 'allowed' : 'denied'}`
    : ''

  return (
    <div className={`ab-perm${open ? ' ab-perm--open' : ''}`}>
      <div className="ab-perm__head">
        <span className="ab-perm__glyph">{line.gutter || '⏺'}</span>
        <span className="ab-perm__title">разрешить вызов</span>
        <span className="ab-perm__tool">{perm.toolName}</span>
        <span className={`ab-perm__badge${badgeCls}`}>{badge}</span>
      </div>

      <pre className="ab-perm__args">{args}</pre>

      {open && (
        <div className="ab-perm__actions">
          <button
            type="button"
            className="ab-perm__btn ab-perm__btn--allow"
            onClick={() => onRespond(perm.id, 'allow')}
          >
            1. разрешить
          </button>
          <button
            type="button"
            className="ab-perm__btn ab-perm__btn--remember"
            title="разрешить и больше не спрашивать про этот инструмент"
            onClick={() => onRespond(perm.id, 'allow', { remember: true })}
          >
            2. разрешить и больше не спрашивать
          </button>
          <button
            type="button"
            className="ab-perm__btn ab-perm__btn--deny"
            onClick={() => onRespond(perm.id, 'deny', { message: reason.trim() || null })}
          >
            3. запретить
          </button>
          <input
            className="ab-perm__reason"
            value={reason}
            placeholder="причина отказа — необязательно"
            onChange={(e) => setReason(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                onRespond(perm.id, 'deny', { message: reason.trim() || null })
              }
            }}
          />
        </div>
      )}
    </div>
  )
}

export default memo(PermissionCard)
